import { CouponInfo, CouponType, ThemeColor, getValidCurrencyType } from '@tencent/wphk-act-base-ui';
import { CouponVersion, ICashCouponInfo } from '../type';
import { getButtonType, getCouponBtnText, getFormattedDiscount, getIsShowButton, getPkgCouponInfo, getStampText, getTipsLeftConfig, getTipsTextRight, isCouponBackgroundDisabled } from '../utils/ui-formatter';
import { getTipsTextRightV2 } from '../utils/ui-formatter-v2';
import { QryAndFormatCouponItem, UtilsTypes } from '@tencent/wphk-act-base-types';
import { mapCouponType } from '../utils/ui-formatter-framework';

export type AdaptCashCouponInfoParams = {
  /** 多语言 */
  couponLang: UtilsTypes.LanguageTypeEnum,
  /** 券数据 - 旧数据源， coupon 和 formattedCoupon二选一 */
  coupon?: ICashCouponInfo,
  /** 券数据 - 新数据源， coupon 和 formattedCoupon二选一， 为调用qryAndFormatCouponList后生产的券 */
  formattedCoupon?: QryAndFormatCouponItem,
  /** 券数据 - 用于覆盖券 ui 属性 */
  extraParams?: Partial<CouponInfo>,
  /** 券版本 - 用于标识新旧券版本 */
  version?: CouponVersion
};

/** 适配现金优惠券 */
export const adaptCashCouponInfo = (params: AdaptCashCouponInfoParams) => {
  const {
    couponLang,
    coupon,
    formattedCoupon,
    extraParams = {},
    version = CouponVersion.old,
  } = params;
  if (formattedCoupon) {
    return adaptCashCouponInfoFramework(couponLang, formattedCoupon, extraParams, version);
  }
  if (coupon) {
    return adaptCashCouponInfoOrigin(couponLang, coupon, extraParams, version);
  }
  console.error('adaptCashCouponInfo 没有券数据');
  return {};
};

/** 适配现金优惠券 - 新数据源为调用qryCouponList后生产的券 */
export const adaptCashCouponInfoFramework = (
  couponLang: UtilsTypes.LanguageTypeEnum,
  coupon: QryAndFormatCouponItem,
  extraParams: Partial<CouponInfo> = {},
  version = CouponVersion.old, // 版本 1 和版本 2
): CouponInfo => {
  // 调用qryCouponList后生产的券， 将QryCouponListItem转成ICashCouponInfo，给 BasicCoupon 使用，解决适配，key 冲突问题
  const formattedCoupon = {
    ...coupon,
    couponType: mapCouponType(coupon.couponType),
    mrchName: coupon.brandName,
    mrchLogo: coupon.brandLogo,
  } as unknown as ICashCouponInfo;
  return adaptCashCouponInfoOrigin(couponLang, formattedCoupon, extraParams, version);
};

/** 适配现金优惠券 - 旧数据源 */
export const adaptCashCouponInfoOrigin = (
  couponLang: UtilsTypes.LanguageTypeEnum,
  coupon: ICashCouponInfo,
  extraParams: Partial<CouponInfo> = {},
  version = CouponVersion.old, // 版本 1 和版本 2
): CouponInfo => {
  let userCouponInfo = {};
  const {
    mrchLogo,
    couponName,
    couponDesc,
    discount,
    mrchName,
    isPkgCoupon,
    isChainCoupon,
    couponType,
    goodsUrl,
    currencyType,
  } = coupon;
  const tipsTextRight = version === 1 ? getTipsTextRight(coupon) : getTipsTextRightV2(coupon);
  const formattedExtraParams = {
    couponType: couponType || CouponType.cash,
    prizeLogo: mrchLogo,
    spLogo: mrchLogo,
    spDesc: mrchName,
    prizeName: couponName,
    prizeDesc: couponDesc,
    themeColor: ThemeColor.green,
    prizeAmountYuan: discount,
    /** 这里可能有问题：如果数据源来自showGoods,那么折扣券的discount是分为单位，getFormattedDiscount这个函数不兼容 */
    prizeDiscount: getFormattedDiscount(coupon, couponLang), // 处理英文-20%off
    isShowButton: getIsShowButton(coupon),
    couponBtnText: getCouponBtnText(coupon),
    isChain: isPkgCoupon || isChainCoupon,
    isExpire: isCouponBackgroundDisabled(coupon.couponState),
    ...getTipsLeftConfig(coupon),
    ...getPkgCouponInfo(coupon.isPkgCoupon, coupon.pkgCount),
    tipsTextRight,
    goodsUrl: goodsUrl || '', // 货物图片，竖状券需要
    currencyType: getValidCurrencyType(currencyType),
  };
  const stampText = getStampText(coupon, coupon.lang);
  userCouponInfo = {
    isShowStamp: stampText,
    stampText,
    buttonType: getButtonType(coupon),
  };
  // @ts-ignore
  const returnCoupon: CouponInfo = { ...coupon, ...formattedExtraParams, ...userCouponInfo, ...extraParams };
  return returnCoupon as CouponInfo;
};
